import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Assessment as StatsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payments as PaidIcon,
  BeachAccess as LeaveIcon
} from '@mui/icons-material';
import compOffService from '../services/compOffService';
import api from '../services/api';

const CompOffApprovals = ({ filterType, selectedMonth, selectedYear, dateRangeStart, dateRangeEnd }) => {
  const [tabValue, setTabValue] = useState(0);
  const [pendingClaims, setPendingClaims] = useState([]);
  const [allClaims, setAllClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [comments, setComments] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [claimToDelete, setClaimToDelete] = useState(null);
  const [settleDialog, setSettleDialog] = useState({ open: false, claim: null });

  useEffect(() => {
    fetchData();
    fetchStatistics();
  }, [tabValue, filterType, selectedMonth, selectedYear, dateRangeStart, dateRangeEnd]);

  // Build date filter params
  const getDateParams = () => {
    if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
      return { filterType: 'daterange', startDate: dateRangeStart, endDate: dateRangeEnd };
    } else if (filterType === 'yearly' && selectedYear) {
      return { filterType: 'yearly', year: selectedYear };
    } else if (selectedMonth) {
      const [y, m] = selectedMonth.split('-');
      return { year: y, month: m };
    }
    return {};
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateParams = getDateParams();
      if (tabValue === 0) {
        const response = await compOffService.getPendingClaims(dateParams);
        setPendingClaims(response.data.claims || []);
      } else {
        const response = await compOffService.getAllClaims(dateParams);
        setAllClaims(response.data.claims || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load comp-off claims');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await compOffService.getStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedClaim) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await compOffService.approveClaim(selectedClaim.id, comments);
      setSuccess(`Comp-off claim approved for ${selectedClaim.employee.fullName}`);
      setActionDialog({ open: false, action: null });
      setSelectedClaim(null);
      setComments('');
      fetchData();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'Failed to approve claim');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedClaim || !comments.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await compOffService.rejectClaim(selectedClaim.id, comments);
      setSuccess(`Comp-off claim rejected for ${selectedClaim.employee.fullName}`);
      setActionDialog({ open: false, action: null });
      setSelectedClaim(null);
      setComments('');
      fetchData();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'Failed to reject claim');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClaim = async () => {
    if (!claimToDelete) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.delete(`/comp-off/${claimToDelete.id}`);
      setSuccess(`Comp-off claim deleted successfully`);
      setDeleteDialogOpen(false);
      setClaimToDelete(null);
      fetchData();
      fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete claim');
      setDeleteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSettle = async (settlementType) => {
    if (!settleDialog.claim) return;
    setLoading(true);
    setError('');
    try {
      await compOffService.settleClaim(settleDialog.claim.id, settlementType);
      setSuccess(settlementType === 'paid' ? 'Marked as Paid' : settlementType === 'leave_taken' ? 'Marked as Leave Taken' : 'Reset to Unsettled');
      setSettleDialog({ open: false, claim: null });
      fetchData();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'Failed to settle claim');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClaim = (claim) => {
    setSelectedClaim(claim);
    setComments(claim.managerComments || '');
    setActionDialog({ 
      open: true, 
      action: claim.status === 'approved' ? 'reject' : 'approve' 
    });
  };

  const openActionDialog = (claim, action) => {
    setSelectedClaim(claim);
    setActionDialog({ open: true, action });
    setComments('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  const ClaimRow = ({ claim, showActions = true }) => (
    <TableRow hover>
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {claim.employee?.fullName || 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
            {new Date(claim.workDate).toLocaleDateString('en-GB')}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
        {new Date(claim.workDate).toLocaleDateString('en-GB')}
      </TableCell>
      <TableCell align="center">
        <Chip
          label={claim.isHalfDay ? 'Half Day' : 'Full Day'}
          size="small"
          color={claim.isHalfDay ? 'warning' : 'primary'}
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        <Tooltip title={claim.reason}>
          <span>
            {claim.reason.length > 50 ? `${claim.reason.substring(0, 50)}...` : claim.reason}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip
            label={claim.status.toUpperCase()}
            color={getStatusColor(claim.status)}
            size="small"
          />
          {claim.status === 'approved' && (
            claim.settlementType === 'paid'
              ? <Chip label="Paid" size="small" icon={<PaidIcon sx={{ fontSize: '0.8rem !important' }} />} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />
              : claim.settlementType === 'leave_taken'
              ? <Chip label="Leave Taken" size="small" icon={<LeaveIcon sx={{ fontSize: '0.8rem !important' }} />} sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
              : <Chip label="Not Settled" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
        {claim.approver ? claim.approver.fullName : '-'}
      </TableCell>
      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
        <Typography variant="body2" color="text.secondary">
          {claim.managerComments || '-'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        {showActions && claim.status === 'pending' ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Tooltip title="Approve">
              <IconButton
                size="small"
                color="success"
                onClick={() => openActionDialog(claim, 'approve')}
              >
                <ApproveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject">
              <IconButton
                size="small"
                color="error"
                onClick={() => openActionDialog(claim, 'reject')}
              >
                <RejectIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : showActions ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            {claim.status === 'approved' && (
              <Tooltip title="Change Settlement">
                <IconButton size="small" color="success" onClick={() => setSettleDialog({ open: true, claim })}>
                  <PaidIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Re-evaluate">
              <IconButton size="small" color="primary" onClick={() => handleEditClaim(claim)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => { setClaimToDelete(claim); setDeleteDialogOpen(true); }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Pending
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {statistics.pendingClaims}
                    </Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Approved
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {statistics.approvedClaims}
                    </Typography>
                  </Box>
                  <ApprovedIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space.between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Rejected
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {statistics.rejectedClaims}
                    </Typography>
                  </Box>
                  <RejectedIcon sx={{ fontSize: 40, color: 'error.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {statistics.totalClaims}
                    </Typography>
                  </Box>
                  <StatsIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ border: '1px solid', borderColor: 'success.light' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">Settled</Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                      {statistics.settledClaims ?? 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {statistics.approvedClaims} approved
                    </Typography>
                  </Box>
                  <PaidIcon sx={{ fontSize: 40, color: 'success.dark', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Comp-Off Approvals
          {statistics && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Total: <strong>{statistics.approvedClaims}</strong> approved &nbsp;·&nbsp;
              Settled: <strong>{statistics.settledClaims ?? 0}</strong>
            </Typography>
          )}
        </Typography>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Pending Approvals
                {pendingClaims.length > 0 && (
                  <Chip
                    label={pendingClaims.length}
                    size="small"
                    color="warning"
                  />
                )}
              </Box>
            }
          />
          <Tab label="All Claims" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Employee</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Work Date</strong></TableCell>
                  <TableCell align="center"><strong>Type</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Reason</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Approved By</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}><strong>Comments</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabValue === 0 ? (
                  pendingClaims.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Alert severity="info" icon={<PendingIcon />}>
                          No pending comp-off claims
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingClaims.map((claim) => (
                      <ClaimRow key={claim.id} claim={claim} showActions={true} />
                    ))
                  )
                ) : (
                  allClaims.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Alert severity="info">
                          No comp-off claims found
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    allClaims.map((claim) => (
                      <ClaimRow key={claim.id} claim={claim} showActions={true} />
                    ))
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !loading && setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this comp-off claim for{' '}
            <strong>{claimToDelete?.employee?.fullName}</strong> dated{' '}
            <strong>{claimToDelete?.workDate ? new Date(claimToDelete.workDate).toLocaleDateString('en-GB') : ''}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleDeleteClaim} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settle Dialog */}
      <Dialog open={settleDialog.open} onClose={() => !loading && setSettleDialog({ open: false, claim: null })}>
        <DialogTitle>Change Settlement</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            <strong>{settleDialog.claim?.employee?.fullName}</strong> —{' '}
            {settleDialog.claim?.workDate ? new Date(settleDialog.claim.workDate).toLocaleDateString('en-GB') : ''}
            {' '}({settleDialog.claim?.isHalfDay ? 'Half Day' : 'Full Day'})
          </Typography>
          {settleDialog.claim?.settlementType !== 'none' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Currently: <strong>{settleDialog.claim?.settlementType === 'paid' ? 'Paid (Cash)' : 'Leave Taken'}</strong>
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
            <Button
              variant={settleDialog.claim?.settlementType === 'paid' ? 'contained' : 'outlined'}
              color="success"
              startIcon={<PaidIcon />}
              onClick={() => handleSettle('paid')}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              Paid (Cash)
            </Button>
            <Button
              variant={settleDialog.claim?.settlementType === 'leave_taken' ? 'contained' : 'outlined'}
              color="primary"
              startIcon={<LeaveIcon />}
              onClick={() => handleSettle('leave_taken')}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              Leave Taken
            </Button>
            <Button
              variant={settleDialog.claim?.settlementType === 'none' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => handleSettle('none')}
              disabled={loading}
              sx={{ flex: 1 }}
            >
              Reset to Unsettled
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleDialog({ open: false, claim: null })} disabled={loading}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ApproveIcon color="success" />
              <Typography variant="h6">Approve Comp-Off Claim</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RejectIcon color="error" />
              <Typography variant="h6">Reject Comp-Off Claim</Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedClaim && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Employee:</strong> {selectedClaim.employee?.fullName}<br />
                <strong>Work Date:</strong> {new Date(selectedClaim.workDate).toLocaleDateString('en-GB')}<br />
                <strong>Type:</strong>{' '}
                <Chip
                  label={selectedClaim.isHalfDay ? 'Half Day' : 'Full Day'}
                  size="small"
                  color={selectedClaim.isHalfDay ? 'warning' : 'primary'}
                  variant="outlined"
                  sx={{ verticalAlign: 'middle' }}
                /><br />
                <strong>Reason:</strong> {selectedClaim.reason}
              </Alert>

              <TextField
                label={actionDialog.action === 'approve' ? 'Comments (Optional)' : 'Reason for Rejection (Required)'}
                multiline
                rows={4}
                fullWidth
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={
                  actionDialog.action === 'approve'
                    ? 'Add any comments for the employee...'
                    : 'Please explain why this claim is being rejected...'
                }
                required={actionDialog.action === 'reject'}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setActionDialog({ open: false, action: null })}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={actionDialog.action === 'approve' ? handleApprove : handleReject}
            variant="contained"
            color={actionDialog.action === 'approve' ? 'success' : 'error'}
            disabled={loading || (actionDialog.action === 'reject' && !comments.trim())}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : actionDialog.action === 'approve' ? (
              'Approve'
            ) : (
              'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompOffApprovals;